import {
  Banner,
  Button,
  Dialog,
  IconButton,
  toast,
  useDebouncedState,
} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {
  type ClipboardEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';

import {trackCrossProjectPinnedRuns} from '../../../services/analytics/crossProjectRunsEvents';
import {useViewAction} from '../../../state/views/hooks';
import * as RunSetActions from '../../../state/views/runSet/actions';
import {MAX_PINNED_RUNS, TOP_NAV_HEIGHT} from '../../../util/constants';
import {maybePluralize} from '../../../util/uihelpers';
import {
  useCurrentRunSetRef,
  usePinnedRunIdsOnly,
} from '../RunSelectorRunSetContext';
import {isAbsoluteUrl, parseRunUrl} from './parseRunUrl';
import {ProjectPicker, type ProjectResolutionResult} from './ProjectPicker';
import {RunsList} from './RunsList';
import {type ProjectOption} from './useCrossProjectOptions';

type CrossProjectRunsDrawerProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

type PendingUrlFill = {
  entityName: string;
  projectName: string;
  runName: string;
};

export const CrossProjectRunsDrawer = ({
  isOpen,
  onOpenChange,
}: CrossProjectRunsDrawerProps) => {
  const runSetRef = useCurrentRunSetRef();
  const pinnedRunIds = usePinnedRunIdsOnly();
  const pinnedRunIdsSet = useMemo(() => new Set(pinnedRunIds), [pinnedRunIds]);
  const isAtMaxPinsLimit = pinnedRunIds.length >= MAX_PINNED_RUNS;

  const bulkUpdatePins = useViewAction(
    runSetRef,
    RunSetActions.setBulkPinnedRuns
  );

  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(
    null
  );
  const [selectedRunIds, setSelectedRunIds] = useState(() => pinnedRunIdsSet);
  const {
    state: runSearch,
    debouncedState: debouncedRunSearch,
    setState: setRunSearch,
  } = useDebouncedState('', 300);
  const [pendingUrlFill, setPendingUrlFill] = useState<PendingUrlFill | null>(
    null
  );
  const cancelUrlFill = useCallback(() => setPendingUrlFill(null), []);

  const toggleRunSelection = useCallback(
    (runId: string) => {
      cancelUrlFill();
      setSelectedRunIds(prev => {
        const next = new Set(prev);
        if (next.has(runId)) {
          next.delete(runId);
        } else {
          next.add(runId);
        }
        return next;
      });
    },
    [cancelUrlFill]
  );

  const onPasteRunUrl = (event: ClipboardEvent<HTMLDivElement>) => {
    const clipboardText = event.clipboardData.getData('text');
    const parsedUrl = parseRunUrl(clipboardText);
    if (parsedUrl == null) {
      if (!isAbsoluteUrl(clipboardText)) {
        return;
      }
      event.preventDefault();
      cancelUrlFill();
      toast.error('Paste a valid W&B run URL.');
      return;
    }

    event.preventDefault();
    setPendingUrlFill(parsedUrl);
  };

  const onResolutionComplete = useCallback(
    ({query, options, loadFailed}: ProjectResolutionResult) => {
      if (pendingUrlFill == null || query !== pendingUrlFill.projectName) {
        return;
      }

      setPendingUrlFill(null);
      if (loadFailed) {
        toast.error('Unable to load projects. Try again.');
        return;
      }

      const project = options.find(
        option =>
          option.entityName.toLowerCase() ===
            pendingUrlFill.entityName.toLowerCase() &&
          option.name.toLowerCase() === pendingUrlFill.projectName.toLowerCase()
      );
      if (project == null) {
        toast.error('Unable to find project');
        return;
      }

      const isSameProject = selectedProject?.internalId === project.internalId;

      setSelectedProject(project);
      setRunSearch(pendingUrlFill.runName);
      setSelectedRunIds(prev =>
        isSameProject ? prev : new Set(pinnedRunIdsSet)
      );
      toast.success('Filled from URL');
    },
    [pendingUrlFill, pinnedRunIdsSet, selectedProject, setRunSearch]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        cancelUrlFill();
      }
      onOpenChange(open);
    },
    [cancelUrlFill, onOpenChange]
  );

  const {runsToPin, runsToUnpin} = useMemo(() => {
    return {
      // Selected but not currently pinned -> should pin
      runsToPin: [...selectedRunIds].filter(id => !pinnedRunIdsSet.has(id)),
      // Currently pinned but not selected -> should unpin
      runsToUnpin: [...pinnedRunIdsSet].filter(id => !selectedRunIds.has(id)),
    };
  }, [pinnedRunIdsSet, selectedRunIds]);
  const hasChanges = runsToPin.length > 0 || runsToUnpin.length > 0;

  const onSubmitRuns = useCallback(() => {
    trackCrossProjectPinnedRuns({
      action: 'Cross-project runs drawer submitted',
      numRunsAdded: runsToPin.length,
      numRunsRemoved: runsToUnpin.length,
    });
    bulkUpdatePins(Array.from(selectedRunIds));
    handleOpenChange(false);
  }, [
    runsToPin.length,
    runsToUnpin.length,
    selectedRunIds,
    bulkUpdatePins,
    handleOpenChange,
  ]);

  const offsetTop =
    window.scrollY < TOP_NAV_HEIGHT ? TOP_NAV_HEIGHT - window.scrollY : 0;

  return (
    <Dialog.Drawer
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      offsetTop={offsetTop}>
      <TailwindContents>
        <div className="flex shrink-0 items-center justify-between gap-8 py-16 px-20 border-b border-wb-border">
          <Dialog.Heading mb={0}>Show runs from another project</Dialog.Heading>
          <div>
            <IconButton
              icon="close"
              aria-label="Close drawer"
              variant="ghost"
              onPress={() => handleOpenChange(false)}
            />
          </div>
        </div>
        <div
          className="flex min-h-0 flex-1 flex-col gap-20 px-20 py-16 overflow-hidden"
          onPaste={onPasteRunUrl}>
          <p className="shrink-0 text-moon-650 dark:text-moon-350">
            Select runs from another project to visualize in line plots in this
            workspace. Runs will be pinned and stay synced to source runs.
          </p>
          {isAtMaxPinsLimit ? (
            <Banner variant="warning" isDismissible={false}>
              <Banner.Body>
                <span className="font-semibold">
                  Pinned runs limit reached.
                </span>{' '}
                You can pin up to {MAX_PINNED_RUNS} runs. Unpin some runs before
                adding runs from another project.
              </Banner.Body>
            </Banner>
          ) : (
            <>
              <ProjectPicker
                selectedProject={selectedProject}
                onSelectProject={project => {
                  cancelUrlFill();
                  setSelectedProject(project);
                  setSelectedRunIds(pinnedRunIdsSet);
                }}
                urlProjectName={pendingUrlFill?.projectName ?? null}
                onResolutionComplete={onResolutionComplete}
                onSearchInputChange={cancelUrlFill}
              />
              {selectedProject && (
                <RunsList
                  selectedProject={selectedProject}
                  selectedRunIds={selectedRunIds}
                  onToggleRun={toggleRunSelection}
                  search={runSearch}
                  debouncedSearch={debouncedRunSearch}
                  onSearchChange={search => {
                    cancelUrlFill();
                    setRunSearch(search);
                  }}
                />
              )}
            </>
          )}
        </div>
        {hasChanges && (
          <div className="p-16 border-t border-wb-border w-full ">
            <Button fullWidth variant="primary" onPress={onSubmitRuns}>
              {getSubmitButtonLabel(runsToPin.length, runsToUnpin.length)}
            </Button>
          </div>
        )}
      </TailwindContents>
    </Dialog.Drawer>
  );
};

function getSubmitButtonLabel(numToPin: number, numToUnpin: number): string {
  if (numToPin > 0 && numToUnpin > 0) {
    return `Unpin ${maybePluralize(numToUnpin, 'run')} and pin ${maybePluralize(numToPin, 'new run')}`;
  }
  if (numToPin > 0) {
    return `Pin ${maybePluralize(numToPin, 'run')}`;
  }
  if (numToUnpin > 0) {
    return `Unpin ${maybePluralize(numToUnpin, 'run')}`;
  }
  return '';
}
